import { TouchesPermisesEnum } from './../../../constantes/touchesPermises.enum';
import { AfterContentInit, Component, ViewEncapsulation } from '@angular/core';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { GlobalStoreService } from '../../../services/global-store/global-store.service';
import { ErreurMappingService } from '../../../services/errors/erreur-mapping.service';
import { SaisieComponent } from '../saisie.component';
import { TypeChangementEnum } from '../../../constantes/type-changement.enum';
import { Validators } from '@angular/forms';
import { CustomValidators } from '../../../validators/custom-validators.validators';
import { CybermetrieCoordonneesService } from '../../../services/cybermetrie/cybermetrie.service';
import { CybermetrieEnum } from '../../../models/cybermetrie/cybermetrie.enum';
import { AccessibiliteService } from '../../../services/accessibilite.service';
import { LangueService } from 'src/app/services/langue/langue.service';
import { Telephones, TelephonesTypeTelephoneEnum } from 'src/app/api/model/telephones.model';
import { EnumUtil } from 'src/app/util/enum-util';

@Component({
  selector: 'app-saisie-telephone',
  templateUrl: './saisie-telephone.component.html',
  styleUrls: ['./saisie-telephone.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SaisieTelephoneComponent extends SaisieComponent implements AfterContentInit {
  // Vérifie si le numéro existe déjà au dossier et empêche la section de devenir invisible
  isSecondaireRequired: boolean = false;
  isEmployeurRequired: boolean = false;

  // Activation de section HTML
  isAfficherInfos = false;
  isAfficherListe = false;
  isSupression = false;
  isSecondNumeroActif: boolean = false;
  isTroisiemeNumeroActif: boolean = false;

  typeNumeroDeux?: TelephonesTypeTelephoneEnum = undefined;
  typeNumeroTrois?: TelephonesTypeTelephoneEnum = undefined;
  choixInput: string[] = [TelephonesTypeTelephoneEnum.SECONDAIRE, TelephonesTypeTelephoneEnum.EMPLOYEUR];
  telephones?: Telephones[];

  constructor(
    erreurMapping: ErreurMappingService,
    globalService: GlobalStoreService,
    navigationService: NavigationService,
    cybermetrieCoordonneesService: CybermetrieCoordonneesService,
    accessibiliteService: AccessibiliteService,
    languageService: LangueService
  ) {
    super(
      erreurMapping,
      globalService,
      navigationService,
      cybermetrieCoordonneesService,
      accessibiliteService,
      languageService,
      TypeChangementEnum.TELEPHONE
    );
  }

  ngAfterContentInit() {
    this.globalStoreService.getUpdateDataForm().subscribe((formData) => {
      this.telephones = formData?.telephones || this.coordonneesClient?.telephones;
      this.isAfficherInfos = this.telephones.length <= 2;
      this.isSupression =
        this.telephones.find((tel) =>
          [TelephonesTypeTelephoneEnum.SECONDAIRE, TelephonesTypeTelephoneEnum.EMPLOYEUR].includes(tel.typeTelephone)
        ) != null;
      this.setTelephone();
      this.verifyRequired();
      this.isAfficherListe = this.verifyAfficherListe();
    });
  }

  private verifyRequired() {
    if (this.coordonneesClient.telephones) {
      this.isSecondaireRequired = this.verifySecondaireRequired();
      this.isEmployeurRequired = Boolean(this.verifyEmployeurRequired() || this.verifyNumeroPosteEmpty());
    }
  }

  private validerChampTelephoneEmployeur(posteNumerique?: string) {
    if (posteNumerique != null) {
      this.isEmployeurRequired = true;
      this.controleFormGroup.controls['employeur'].addValidators(Validators.required);
      this.controleFormGroup.controls['employeur'].updateValueAndValidity();
    } else if (!this.verifyEmployeurRequired()) {
      this.removeChampTelephoneEmployeur();
    }
  }

  // Les attributs min et max du component dsd-input ont pas marchés avec les regles d'affichage requis. On a du garder l'implementation custom.
  getPosteNumerique($event: KeyboardEvent) {
    this.validerTouche($event);
    const poste = this.construirPoste($event);
    this.validerChampTelephoneEmployeur(poste);
    this.controleFormGroup?.get('numeroPoste')?.setValue(poste);
  }

  private validerTouche($event: KeyboardEvent) {
    const codeTouche = $event?.keyCode;
    if (!Object.values(TouchesPermisesEnum).includes(codeTouche)) {
      $event.preventDefault();
    }
  }

  private construirPoste($event: KeyboardEvent): string | undefined {
    const key = $event?.key;
    const target = $event.target as HTMLInputElement;
    const character: string = target.value;
    if (['DELETE', 'BACKSPACE'].includes(key.toUpperCase()) && (character.length === 1 || this.isAllSelected($event)))
      return undefined;
    const index = target.selectionStart;
    const poste = character.substring(0, index) + key + character.substring(index);
    return poste.replace(/\D/g, '').substring(0, 6);
  }

  // Valide si tout le texte de l'input est selectionée
  private isAllSelected(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const character: string = target.value;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    return start === 0 && end === character.length;
  }

  private removeChampTelephoneEmployeur() {
    this.isEmployeurRequired = false;
    this.controleFormGroup.controls['employeur'].clearValidators();
    this.controleFormGroup.controls['employeur'].addValidators([
      Validators.maxLength(12),
      CustomValidators.numeroTelephoneHogan,
      CustomValidators.caracteresValidesBackendSeulement,
    ]);
    this.controleFormGroup.controls['employeur'].updateValueAndValidity();
  }

  private verifyAfficherListe() {
    // On affiche la/les liste(s) quand les numéros secondaire ET employeur sont absents.
    if (!this.coordonneesClient.telephones) return true;
    return !this.coordonneesClient.telephones.find((tel) => {
      if (tel.numeroTelephone == null || tel.typeTelephone == null) {
        return false;
      }
      return [TelephonesTypeTelephoneEnum.SECONDAIRE, TelephonesTypeTelephoneEnum.EMPLOYEUR].includes(
        tel.typeTelephone
      );
    });
  }

  private verifyEmployeurRequired(): boolean {
    // Le numéro d'employeur est requis SI et SEULEMENT SI
    // 1. Le numéro de poste n'est pas vide (Vérifier dans validerChampTelephoneEmployeur)
    // 2. Le numéro d'employeur vient du SI
    return (
      this.coordonneesClient.telephones.find(
        (telephone) =>
          telephone.typeTelephone === TelephonesTypeTelephoneEnum.EMPLOYEUR && telephone.numeroTelephone != null
      ) !== undefined
    );
  }

  private verifySecondaireRequired(): boolean {
    return (
      this.coordonneesClient.telephones.find(
        (telephone) =>
          telephone.typeTelephone === TelephonesTypeTelephoneEnum.SECONDAIRE && telephone.numeroTelephone != null
      ) !== undefined
    );
  }

  private verifyNumeroPosteEmpty(): boolean {
    const poste = this.controleFormGroup.controls['numeroPoste'].value;
    return poste && poste.trim() !== '';
  }

  private setTelephone(): void {
    const telSecondaire = this.telephones?.find((tel) => tel.typeTelephone === TelephonesTypeTelephoneEnum.SECONDAIRE);
    const telEmployeur = this.telephones?.find((tel) => tel.typeTelephone === TelephonesTypeTelephoneEnum.EMPLOYEUR);
    // Si on trouve un des deux, on veut placer la valeur dans typeNumeroDeux
    // telSecondaire || telEmployeur assure qu'on prend toujours telSecondaire s'il est disponible
    if (telSecondaire || telEmployeur) {
      this.typeNumeroDeux = (telSecondaire || telEmployeur).typeTelephone;
      this.setAutoComplete(SaisieComponent.CHAMP_TYPE_TEL.get(this.typeNumeroDeux));
      this.isSecondNumeroActif = true;
    }
    // Si on trouve les deux, on sait que la valeur de typeNumeroTrois est employeur.
    if (telSecondaire && telEmployeur) {
      this.typeNumeroTrois = telEmployeur.typeTelephone;
      this.setAutoComplete(this.typeNumeroTrois);
      this.isTroisiemeNumeroActif = true;
    }
  }

  // Sur changement de choix dans la seconde liste déroulante,
  // Retire le numéro du type changé
  // Corrige la liste de la troisième zone
  onChoixChange(event: Event) {
    if (this.typeNumeroDeux != null) {
      this.controleFormGroup.controls[SaisieComponent.CHAMP_TYPE_TEL.get(this.typeNumeroDeux)].reset();
      if (this.typeNumeroDeux === TelephonesTypeTelephoneEnum.EMPLOYEUR) {
        this.controleFormGroup.controls['numeroPoste'].reset();
        this.removeChampTelephoneEmployeur();
      }
    }
    const target = event.target as HTMLSelectElement;
    this.typeNumeroDeux = EnumUtil.getStringEnumKeyByStringValueOrThrow(
      TelephonesTypeTelephoneEnum,
      target.options[target.options.selectedIndex].value
    );
    this.typeNumeroTrois = this.choixInput
      .map((choix) => EnumUtil.getStringEnumKeyByStringValueOrThrow(TelephonesTypeTelephoneEnum, choix))
      .find((telType) => telType !== this.typeNumeroDeux);
    this.setAutoComplete(SaisieComponent.CHAMP_TYPE_TEL.get(this.typeNumeroDeux));
    const extra = new Map<string, string>().set(
      CybermetrieEnum.EXTRA_ATTRIBUT,
      this.typeNumeroDeux.toLocaleLowerCase()
    );
    this.cybermetrieCoordonneesService.envoyerEvenementPersonnalise(
      CybermetrieEnum.CATEGORIE_TRANSACTION,
      CybermetrieEnum.ACTION_AJOUTER,
      CybermetrieEnum.LABEL_MODIFIER_TELEPHONE,
      0,
      '',
      extra
    );
  }

  // Active la troisième zone et corrige la liste de la seconde zone
  ajouterDernierChamps() {
    this.typeNumeroTrois = this.choixInput
      .map((choix) => EnumUtil.getStringEnumKeyByStringValueOrThrow(TelephonesTypeTelephoneEnum, choix))
      .find((telType) => telType !== this.typeNumeroDeux);
    this.isTroisiemeNumeroActif = true;
    this.setAutoComplete(SaisieComponent.CHAMP_TYPE_TEL.get(this.typeNumeroTrois));
    this.accessibiliteService.setFocus('#' + this.typeNumeroTrois);
    const extra = new Map<string, string>();

    if (this.typeNumeroDeux === TelephonesTypeTelephoneEnum.SECONDAIRE) {
      extra.set(CybermetrieEnum.EXTRA_ATTRIBUT, CybermetrieEnum.EMPLOYEUR);
    } else {
      extra.set(CybermetrieEnum.EXTRA_ATTRIBUT, CybermetrieEnum.SECONDAIRE);
    }

    this.cybermetrieCoordonneesService.envoyerEvenementPersonnalise(
      CybermetrieEnum.CATEGORIE_TRANSACTION,
      CybermetrieEnum.ACTION_AJOUTER,
      CybermetrieEnum.LABEL_MODIFIER_TELEPHONE,
      0,
      '',
      extra
    );
  }

  private setAutoComplete(type: string) {
    setTimeout(() => {
      const input = document.getElementById(type);
      if (!input) return;
      input.setAttribute(
        'autocomplete',
        type === TelephonesTypeTelephoneEnum.SECONDAIRE ? 'secondary-phone' : 'work-phone'
      );
    }, 200);
  }
}
